/** Currency codes the UI knows how to display. Adds-on at the end keep
 *  the ISO 4217 alpha-3 convention so the suffix matches the source data
 *  directly. NetSuite GL exports surface these verbatim in
 *  `Currency: Name`; QBO ingest defaults to CAD. */
export type Currency =
  | 'CAD'
  | 'USD'
  | 'EUR'
  | 'GBP'
  | 'AUD'
  | 'JPY'
  | 'INR'
  | 'ARS'
  | 'BRL'
  | 'MXN'
  | 'CHF'
  | 'CNY';

/** Default currency assumed when none is present on a row. Today's
 *  practitioner-facing flows are CAD-heavy; flip per-engagement when
 *  multi-currency tenants land. */
export const DEFAULT_CURRENCY: Currency = 'CAD';

/** Normalize an arbitrary string into a known `Currency`, defaulting to
 *  CAD when the input is empty, unknown, or null. Used at the boundary
 *  where we read currency from evidence dicts / API rows. */
export function coerceCurrency(value: string | null | undefined): Currency {
  if (!value) return DEFAULT_CURRENCY;
  const upper = value.trim().toUpperCase();
  if (
    upper === 'CAD' ||
    upper === 'USD' ||
    upper === 'EUR' ||
    upper === 'GBP' ||
    upper === 'AUD' ||
    upper === 'JPY' ||
    upper === 'INR' ||
    upper === 'ARS' ||
    upper === 'BRL' ||
    upper === 'MXN' ||
    upper === 'CHF' ||
    upper === 'CNY'
  ) {
    return upper;
  }
  return DEFAULT_CURRENCY;
}

/** Format `value` with a `$` prefix and the ISO currency code suffix —
 *  e.g. `$1,234.56 CAD`, `$795,414.16 USD`. The suffix is what makes
 *  values from mixed-currency runs unambiguous; the `$` glyph is a
 *  generic money marker (using `£` / `€` per-currency would split the
 *  visual alignment in tables and is less scannable). */
export function formatCurrency(
  value: string | number | null | undefined,
  currency: Currency = DEFAULT_CURRENCY,
): string {
  const n = Number(value);
  const safe = Number.isFinite(n) ? n : 0;
  const formatted = new Intl.NumberFormat('en-CA', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(safe);
  return `$${formatted} ${currency}`;
}

/** Currency-bucketed sum input. Used by the aggregation formatter when
 *  a single column rolls up values that might be in different
 *  currencies (e.g. Suppliers tab total recovery across NVG's CAD +
 *  GBP + USD findings). */
export interface CurrencySum {
  amount: number;
  currency: Currency;
}

/** Format a multi-currency aggregation. Picks the largest-magnitude
 *  currency as the headline and tags how many other currencies sit
 *  underneath, e.g. `$1,234.56 CAD (+ 2 other)`. Honest about the
 *  fact that summing across currencies is meaningless without a rate
 *  — reviewers see the marker and know to dig into the breakdown.
 *
 *  Returns `$0.00 CAD` when no positive sums exist. */
export function formatCurrencySums(sums: readonly CurrencySum[]): string {
  const nonZero = sums.filter((s) => Math.abs(s.amount) > 0);
  if (nonZero.length === 0) return formatCurrency(0, DEFAULT_CURRENCY);
  const sorted = [...nonZero].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
  const primary = formatCurrency(sorted[0].amount, sorted[0].currency);
  const others = sorted.length - 1;
  return others > 0 ? `${primary} (+ ${others} other)` : primary;
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
  });
}

export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-CA', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}
